<template>
  <div class="p-6 dark:text-gray-50" v-if="canAddWiFiProfile || user?.is_staff">
    <div class="flex space-x-3 mb-6">
      <h1 class="text-2xl font-semibold dark:text-white">Add WiFi Profile</h1>
    </div>

    <form @submit.prevent="submitForm" class="space-y-4 bg-white/80 dark:bg-gray-800/70 backdrop-blur-md dark:text-bg-gray dark:border-gray-800 p-6 rounded-lg shadow-sm border border-gray-200">

      <div
        v-if="formError"
        class="rounded border border-red-300 bg-red-50 dark:bg-red-900/30 dark:border-red-700 px-4 py-3 text-sm text-red-700 dark:text-red-200"
        role="alert"
      >
        {{ formError }}
      </div>

      <div class="flex flex-col">
        <label for="name_override" class="mb-1 font-medium dark:text-white">Name Override</label>
        <input
          id="name_override"
          v-model="form.name_override"
          type="text"
          class="w-full border border-gray-300 dark:bg-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2"
          :class="[fieldErrors.name_override ? 'ring-2 ring-red-500 focus:ring-red-500' : 'focus:ring-blue-500']"
        />
        <FieldError :message="fieldErrors.name_override" />
      </div>

      <div class="flex flex-col">
        <label for="description" class="mb-1 font-medium dark:text-white">Description</label>
        <textarea
          id="description"
          v-model="form.description"
          rows="3"
          class="w-full border border-gray-300 dark:bg-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2"
          :class="[fieldErrors.description ? 'ring-2 ring-red-500 focus:ring-red-500' : 'focus:ring-blue-500']"
        />
        <FieldError :message="fieldErrors.description" />
      </div>

      <div class="flex flex-col">
        <label for="ssid" class="mb-1 font-medium dark:text-white">
          SSID<span class="pl-1 text-red-500">*</span>
        </label>
        <input
          id="ssid"
          v-model="form.ssid"
          type="text"
          maxlength="32"
          required
          class="w-full border border-gray-300 dark:bg-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2"
          :class="[fieldErrors.ssid ? 'ring-2 ring-red-500 focus:ring-red-500' : 'focus:ring-blue-500']"
        />
        <FieldError :message="fieldErrors.ssid" />
      </div>

      <div class="flex flex-col">
        <label for="auth_type" class="mb-1 font-medium dark:text-white">
          Auth Type<span class="pl-1 text-red-500">*</span>
        </label>
        <GlassSelect
          v-model="form.auth_type"
          :options="authTypeOptions"
          valueKey="value"
          labelKey="label"
          placeholder="Select auth type"
          :error="!!fieldErrors.auth_type"
        />
        <FieldError :message="fieldErrors.auth_type" />
      </div>

      <div class="flex flex-col">
        <label for="frequency_band" class="mb-1 font-medium dark:text-white">
          Frequency Band<span class="pl-1 text-red-500">*</span>
        </label>
        <GlassSelect
          v-model="form.frequency_band"
          :options="frequencyBandOptions"
          valueKey="value"
          labelKey="label"
          placeholder="Select band"
          :error="!!fieldErrors.frequency_band"
        />
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
          The AP must broadcast on the chosen band(s). 2.4 GHz has longer range; 5 GHz is faster but shorter range. Pick "Both" if unsure. Note: using both bands requires two WiFi dongles installed on the probe (one per band).
        </p>
        <FieldError :message="fieldErrors.frequency_band" />
      </div>

      <div v-if="form.auth_type === 'wpa_enterprise'" class="space-y-4 border-l-4 border-blue-500 pl-4">
        <div class="flex flex-col">
          <label for="eap_method" class="mb-1 font-medium dark:text-white">
            EAP Method<span class="pl-1 text-red-500">*</span>
          </label>
          <GlassSelect
            v-model="form.eap_method"
            :options="eapMethodOptions"
            placeholder="Select EAP method"
            :error="!!fieldErrors.eap_method"
          />
          <FieldError :message="fieldErrors.eap_method" />
        </div>

        <div class="flex flex-col">
          <label for="phase2" class="mb-1 font-medium dark:text-white">Phase 2</label>
          <input
            id="phase2"
            v-model="form.phase2"
            type="text"
            placeholder="MSCHAPV2"
            class="w-full border border-gray-300 dark:bg-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2"
            :class="[fieldErrors.phase2 ? 'ring-2 ring-red-500 focus:ring-red-500' : 'focus:ring-blue-500']"
          />
          <FieldError :message="fieldErrors.phase2" />
        </div>

        <div class="flex flex-col">
          <label for="identity" class="mb-1 font-medium dark:text-white">
            Identity<span class="pl-1 text-red-500">*</span>
          </label>
          <input
            id="identity"
            v-model="form.identity"
            type="text"
            required
            class="w-full border border-gray-300 dark:bg-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2"
            :class="[fieldErrors.identity ? 'ring-2 ring-red-500 focus:ring-red-500' : 'focus:ring-blue-500']"
          />
          <FieldError :message="fieldErrors.identity" />
        </div>

        <div class="flex flex-col">
          <label for="password" class="mb-1 font-medium dark:text-white">
            Password<span class="pl-1 text-red-500">*</span>
          </label>
          <input
            id="password"
            v-model="form.password"
            type="password"
            required
            class="w-full border border-gray-300 dark:bg-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2"
            :class="[fieldErrors.password ? 'ring-2 ring-red-500 focus:ring-red-500' : 'focus:ring-blue-500']"
          />
          <FieldError :message="fieldErrors.password" />
        </div>
      </div>

      <div v-if="form.auth_type === 'wpa_psk' || form.auth_type === 'wpa3_sae'" class="space-y-4 border-l-4 border-blue-500 pl-4">
        <div class="flex flex-col">
          <label for="wifi_password" class="mb-1 font-medium dark:text-white">
            WiFi Password<span class="pl-1 text-red-500">*</span>
          </label>
          <input
            id="wifi_password"
            v-model="form.wifi_password"
            type="password"
            minlength="8"
            maxlength="63"
            required
            placeholder="8-63 characters"
            class="w-full border border-gray-300 dark:bg-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2"
            :class="[fieldErrors.psk ? 'ring-2 ring-red-500 focus:ring-red-500' : 'focus:ring-blue-500']"
          />
          <p v-if="form.auth_type === 'wpa_psk'" class="text-xs text-gray-500 dark:text-gray-400 mt-1">
            The PSK is generated from the SSID + password using PBKDF2 (WPA2 standard).
          </p>
          <p v-else class="text-xs text-gray-500 dark:text-gray-400 mt-1">
            The passphrase is stored encrypted and used directly by wpa_supplicant for SAE.
          </p>
          <FieldError :message="fieldErrors.psk" />
        </div>
      </div>

      <div class="flex justify-end space-x-2">
        <button
          type="button"
          @click="$router.push({ name: 'WiFiProfiles' })"
          class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 transition duration-200"
        >
          Cancel
        </button>
        <button
          type="submit"
          class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
        >
          Create WiFi Profile
        </button>
      </div>
    </form>
  </div>

  <PageError
    v-if="permsLoaded && !canAddWiFiProfile && !user?.is_staff"
    :error="errorMessages['403']"
    :errorCode="'403'"
  />
</template>

<script>
import { createWiFiProfile } from "@/services/wifiProfileService";
import FieldError from "@/components/FieldError.vue";
import PageError from "@/components/PageError.vue";
import GlassSelect from "@/components/GlassSelect.vue";
import { useStore } from "@/store";
import { errorMessages } from "@/utils/errorMessages";
import { useMultiTenant } from "@/composables/useMultiTenant";
import { wpa2PskFromPassphrase } from "@/utils/wpa";

export default {
  name: "AddWiFiProfile",
  components: {
    FieldError,
    PageError,
    GlassSelect,
  },
  setup() {
    const tenant = useMultiTenant();
    return { tenant };
  },
  data() {
    return {
      errorMessages: errorMessages,
      fieldErrors: {},
      formError: "",
      canAddWiFiProfile: false,
      form: {
        name_override: "",
        description: "",
        ssid: "",
        auth_type: "wpa_enterprise",
        eap_method: "PEAP",
        phase2: "",
        identity: "",
        password: "",
        wifi_password: "",
        frequency_band: "both",
      },
      authTypeOptions: [
        { value: "wpa_enterprise", label: "WPA-Enterprise" },
        { value: "wpa_psk", label: "WPA2-PSK" },
        { value: "wpa3_sae", label: "WPA3-SAE" },
      ],
      frequencyBandOptions: [
        { value: "both", label: "Both (2.4 GHz + 5 GHz)" },
        { value: "2_4", label: "2.4 GHz only" },
        { value: "5", label: "5 GHz only" },
      ],
      eapMethodOptions: ["PEAP", "TLS", "TTLS"],
    };
  },
  computed: {
    user() {
      return useStore.getUser();
    },
    permsLoaded() {
      return useStore.permissionsLoadedRef.value;
    },
  },
  async created() {
    try {
      await this.tenant.fetchOrganizations();
      await this.tenant.loadInitialData(() => Promise.resolve());
    } catch (error) {
      console.error("Error initializing tenant:", error);
    }
    const q = this.$route.query;
    if (q.ssid) this.form.ssid = String(q.ssid);
    if (q.auth_type && this.authTypeOptions.some(o => o.value === q.auth_type)) {
      this.form.auth_type = String(q.auth_type);
    }
  },
  watch: {
    user: {
      handler(user) {
        if (user) {
          this.checkPermissions();
        }
      },
      immediate: true,
    },
  },
  methods: {
    async checkPermissions() {
      if (this.user) {
        this.canAddWiFiProfile = await useStore.hasPermission("Can add WiFi Profile");
      }
    },
    async buildPayload() {
      const payload = {
        ssid: this.form.ssid,
        auth_type: this.form.auth_type,
        frequency_band: this.form.frequency_band,
      };
      if (this.form.name_override) payload.name_override = this.form.name_override;
      if (this.form.description) payload.description = this.form.description;

      if (this.form.auth_type === "wpa_enterprise") {
        payload.eap_method = this.form.eap_method;
        payload.identity = this.form.identity;
        payload.password = this.form.password;
        if (this.form.phase2) payload.phase2 = this.form.phase2;
      } else if (this.form.auth_type === "wpa_psk") {
        payload.psk = await wpa2PskFromPassphrase(this.form.ssid, this.form.wifi_password);
      } else if (this.form.auth_type === "wpa3_sae") {
        payload.psk = this.form.wifi_password;
      }
      return payload;
    },
    async submitForm() {
      this.fieldErrors = {};
      this.formError = "";
      try {
        const payload = await this.buildPayload();
        const created = await createWiFiProfile(payload, this.tenant.currentSchemaName?.value);
        this.$router.push({
          name: "WiFiProfileDetails",
          params: { id: created.id },
          query: this.tenant.addSchemaParam(),
        });
      } catch (error) {
        const errData = error?.response?.data;
        const formMessages = [];
        const formFieldKeys = new Set(Object.keys(this.form));
        if (errData && typeof errData === "object") {
          Object.keys(errData).forEach((key) => {
            const raw = errData[key];
            const value = Array.isArray(raw) ? raw[0] : raw;
            if (key === "non_field_errors" || key === "detail") {
              if (value) formMessages.push(String(value));
            } else if (formFieldKeys.has(key) || key === "psk") {
              this.fieldErrors[key] = value;
            } else if (value) {
              formMessages.push(String(value));
            }
          });
        } else if (error?.message) {
          formMessages.push(error.message);
        }
        if (formMessages.length) {
          this.formError = formMessages.join(" ");
        } else if (!Object.keys(this.fieldErrors).length) {
          this.formError = "Could not create WiFi profile. Please check the fields and try again.";
        }
        console.error("Error creating WiFi profile:", error);
      }
    },
  },
};
</script>
